import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PrescriptionForm = ({ isOpen, onClose, onSubmit, editingPrescription = null }) => {
  const [formData, setFormData] = useState({
    patientId: editingPrescription?.patientId || '',
    medication: editingPrescription?.medication || '',
    dosage: editingPrescription?.dosage || '',
    frequency: editingPrescription?.frequency || '',
    duration: editingPrescription?.duration || '',
    instructions: editingPrescription?.instructions || '',
    priority: editingPrescription?.priority || 'normal',
    pharmacyId: editingPrescription?.pharmacyId || '',
    refills: editingPrescription?.refills || '0'
  });

  const [searchResults, setSearchResults] = useState([]);
  const [showInteractions, setShowInteractions] = useState(false);
  const [showAllergies, setShowAllergies] = useState(false);

  const patientOptions = [
    { value: 'p001', label: 'Sarah Johnson - DOB: 03/15/1985' },
    { value: 'p002', label: 'Michael Chen - DOB: 07/22/1978' },
    { value: 'p003', label: 'Emily Rodriguez - DOB: 11/08/1992' },
    { value: 'p004', label: 'David Thompson - DOB: 05/14/1965' }
  ];

  const frequencyOptions = [
    { value: 'once_daily', label: 'Once daily' },
    { value: 'twice_daily', label: 'Twice daily' },
    { value: 'three_times_daily', label: 'Three times daily' },
    { value: 'four_times_daily', label: 'Four times daily' },
    { value: 'every_6_hours', label: 'Every 6 hours' },
    { value: 'every_8_hours', label: 'Every 8 hours' },
    { value: 'as_needed', label: 'As needed' }
  ];

  const priorityOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const pharmacyOptions = [
    { value: 'ph001', label: 'CVS Pharmacy - 123 Main St' },
    { value: 'ph002', label: 'Walgreens - 456 Oak Ave' },
    { value: 'ph003', label: 'Rite Aid - 789 Pine Rd' },
    { value: 'ph004', label: 'Local Pharmacy - 321 Elm St' }
  ];

  const mockMedications = [
    { name: 'Lisinopril', dosages: ['5mg', '10mg', '20mg'], category: 'ACE Inhibitor' },
    { name: 'Metformin', dosages: ['500mg', '850mg', '1000mg'], category: 'Antidiabetic' },
    { name: 'Atorvastatin', dosages: ['10mg', '20mg', '40mg', '80mg'], category: 'Statin' },
    { name: 'Amlodipine', dosages: ['2.5mg', '5mg', '10mg'], category: 'Calcium Channel Blocker' }
  ];

  const mockAllergies = [
    { medication: 'Penicillin', severity: 'Severe', reaction: 'Anaphylaxis' },
    { medication: 'Sulfa drugs', severity: 'Moderate', reaction: 'Rash' }
  ];

  const mockInteractions = [
    { medication: 'Warfarin', severity: 'High', description: 'Increased bleeding risk' },
    { medication: 'Digoxin', severity: 'Moderate', description: 'Monitor levels closely' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'medication' && value?.length > 2) {
      const filtered = mockMedications?.filter(med => 
        med?.name?.toLowerCase()?.includes(value?.toLowerCase())
      );
      setSearchResults(filtered);
    } else if (field === 'medication') {
      setSearchResults([]);
    }
  };

  const handleMedicationSelect = (medication) => {
    setFormData(prev => ({ ...prev, medication: medication?.name }));
    setSearchResults([]);
    setShowInteractions(true);
    setShowAllergies(true);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {editingPrescription ? 'Edit Prescription' : 'Create New Prescription'}
          </h2>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Patient Selection */}
            <div className="lg:col-span-2">
              <Select
                label="Select Patient"
                options={patientOptions}
                value={formData?.patientId}
                onChange={(value) => handleInputChange('patientId', value)}
                searchable
                required
              />
            </div>

            {/* Medication Search */}
            <div className="lg:col-span-2 relative">
              <Input
                label="Medication"
                type="text"
                placeholder="Search for medication..."
                value={formData?.medication}
                onChange={(e) => handleInputChange('medication', e?.target?.value)}
                required
              />
              
              {searchResults?.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-popover border border-border rounded-md shadow-modal z-10 mt-1">
                  {searchResults?.map((med, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleMedicationSelect(med)}
                      className="w-full text-left px-4 py-3 hover:bg-muted transition-clinical border-b border-border last:border-b-0"
                    >
                      <div className="font-medium text-foreground">{med?.name}</div>
                      <div className="text-sm text-muted-foreground">{med?.category}</div>
                      <div className="text-xs text-muted-foreground">Available: {med?.dosages?.join(', ')}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dosage and Frequency */}
            <Input
              label="Dosage"
              type="text"
              placeholder="e.g., 10mg"
              value={formData?.dosage}
              onChange={(e) => handleInputChange('dosage', e?.target?.value)}
              required
            />

            <Select
              label="Frequency"
              options={frequencyOptions}
              value={formData?.frequency}
              onChange={(value) => handleInputChange('frequency', value)}
              required
            />

            {/* Duration and Refills */}
            <Input
              label="Duration"
              type="text"
              placeholder="e.g., 30 days"
              value={formData?.duration}
              onChange={(e) => handleInputChange('duration', e?.target?.value)}
              required
            />

            <Input
              label="Number of Refills"
              type="number"
              min="0"
              max="5"
              value={formData?.refills}
              onChange={(e) => handleInputChange('refills', e?.target?.value)}
            />

            {/* Priority and Pharmacy */}
            <Select
              label="Priority"
              options={priorityOptions}
              value={formData?.priority}
              onChange={(value) => handleInputChange('priority', value)}
            />

            <Select
              label="Preferred Pharmacy"
              options={pharmacyOptions}
              value={formData?.pharmacyId}
              onChange={(value) => handleInputChange('pharmacyId', value)}
              searchable
            />

            {/* Instructions */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Special Instructions
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                rows="3"
                placeholder="Additional instructions for the patient..."
                value={formData?.instructions}
                onChange={(e) => handleInputChange('instructions', e?.target?.value)}
              />
            </div>
          </div>

          {/* Allergy Alerts */}
          {showAllergies && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="AlertTriangle" size={20} className="text-error" />
                <h3 className="font-medium text-error">Patient Allergies</h3>
              </div>
              <div className="space-y-2">
                {mockAllergies?.map((allergy, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-foreground">{allergy?.medication}</span>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        allergy?.severity === 'Severe' ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                      }`}>
                        {allergy?.severity}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{allergy?.reaction}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Drug Interactions */}
          {showInteractions && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="AlertCircle" size={20} className="text-warning" />
                <h3 className="font-medium text-warning">Potential Drug Interactions</h3>
              </div>
              <div className="space-y-2">
                {mockInteractions?.map((interaction, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-foreground">{interaction?.medication}</span>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        interaction?.severity === 'High' ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                      }`}>
                        {interaction?.severity}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">{interaction?.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" iconName="Save" iconPosition="left">
              {editingPrescription ? 'Update Prescription' : 'Create Prescription'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrescriptionForm;